import { useState, useEffect } from 'react';
import { useDataFetch } from '../../../hooks/use-data-fetch';
import { WEB_BASE_URL } from '../../../config';
import { useUser } from '../../../context/UserContext';

// Interface for network user data
export interface NetworkUser {
  network: {
    loyalty_status: boolean;
  };
  multyhealth_code: string;
  name: string;
  email: string;
  id: string;
  createdAt: string;
  referenced_by_count: number;
  networkLevel: string;
  last_rank:any;
  cellphone:string;
  monthPoints: number;
  total_registered_users:number;
}

/**
 * Hook personalizado para obtener la red de usuarios
 * @param initialUserId ID del usuario inicial (opcional, si no se proporciona usa el usuario logueado)
 * @returns Estado de carga, error, datos de la red y funciones relacionadas
 */
export const useNetwork = () => {
  const { userData } = useUser()
  const { getData } = useDataFetch();
  
  // Estados para navegación y nivel de red
  const [networkUsers, setNetworkUsers] = useState<NetworkUser[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | undefined>();
  const [navigationHistory, setNavigationHistory] = useState<string[]>([]);
  const [currentLevel, setCurrentLevel] = useState<number>(1);
  const [visitedUsers, setVisitedUsers] = useState<NetworkUser[]>([]);

  // Consultar la red de usuarios
  const {
    data,
    isLoading,
    isError,
    error,
    refetch
  } = getData(
    ["network", currentUserId],
    `/users/network/with/id?userid=${currentUserId ?? ''}`,
    {
      enabled: true,
      refetchOnWindowFocus: false
    }
  );

  // Consultar usuarios por nivel para estadísticas
  const {
    data: levelData,
    isLoading: isLevelLoading,
    isError: isLevelError
  } = getData(
    ["users_by_level"],
    `/users/users_by_level`,
    {
      enabled: true,
      refetchOnWindowFocus: false
    }
  );
  // Actualizar el estado cuando se reciben los datos
  useEffect(() => {
    if (data?.data?.user) {
      // Update networkLevel for each user based on current navigation level
      const usersWithLevel = data.data.user.map((user: any) => ({
        ...user,
        id: user._id,
        networkLevel: `Nivel ${currentLevel}`
      }));
      setNetworkUsers(usersWithLevel);
    }
  }, [data, currentLevel]);

  // Procesar datos de niveles para el ProfileCard
  const networkLevels = levelData?.data ? Object.keys(levelData.data.totalCountByLevel).map((levelKey: string) => ({
    level: parseInt(levelKey),
    totalUsers: levelData.data.totalCountByLevel[levelKey] || 0,
    activeUsers: levelData.data.activeCountByLevel[levelKey] || 0
  })) : []; 

  const totalUsers = Object.values(
    levelData?.data?.totalCountByLevel ?? {}
  ).reduce<number>((acc, count) => acc + Number(count), 0);

  const totalActiveUsers = Object.values(
    levelData?.data?.activeCountByLevel ?? {}
  ).reduce<number>((acc, count) => acc + Number(count), 0);

  
  // Función para cargar la red de un usuario específico
  const loadUserNetwork = (userId: string) => {  
    // Buscar el usuario en la lista actual antes de cambiar
    const userToAdd = networkUsers.find(user => user.id === userId);
    if (userToAdd && !visitedUsers.find(visited => visited.id === userId)) {
      setVisitedUsers(prev => [...prev, userToAdd]);
    }
    setNavigationHistory(prev => [...prev, userId]);
    
    setCurrentUserId(userId);
    refetch();
    setCurrentLevel(prev => prev + 1);
  };

  // Función para volver a cargar mi red
  const loadMyNetwork = () => {
    setCurrentUserId('');
    setNavigationHistory([]);
    setCurrentLevel(1);
    setVisitedUsers([]);
    refetch();
  };

  // Función para refrescar los datos
  const refreshNetwork = () => {
    refetch();
  };

  // Function to copy referral link to clipboard
  const copyReferralLink = () => {
    const referralLink = `${WEB_BASE_URL}/sign-up?code=${userData?.multyhealth_code}`;
    navigator.clipboard.writeText(referralLink);
    alert("¡Enlace copiado al portapapeles!");
  };

  // Function to share via WhatsApp
  const shareViaWhatsApp = () => {
    const referralLink = `${WEB_BASE_URL}/sign-up?code=${userData?.multyhealth_code}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
      `¡Únete a MultyHealth usando mi código de referido! ${referralLink}`
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  // Function to share via Email
  const shareViaEmail = () => {
    const referralLink = `${WEB_BASE_URL}/sign-up?code=${userData?.multyhealth_code}`;
    const emailSubject = "Únete a MultyHealth";
    const emailBody = `¡Hola! Te invito a unirte a MultyHealth usando mi código de referido. Regístrate aquí: ${referralLink}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.open(mailtoUrl, "_blank");
  };


  // Función para navegar a un nivel específico usando breadcrumb
  const navigateToLevel = (userId: string | null, levelIndex: number) => {
    if (levelIndex === -1) {
      // Volver al inicio (mi red)
      loadMyNetwork();
    } else {
      // Navegar a un nivel específico
      const targetUserId = navigationHistory[levelIndex];
      if (targetUserId) {
        setCurrentUserId(targetUserId);
        setNavigationHistory(prev => prev.slice(0, levelIndex + 1));
        setCurrentLevel(levelIndex + 2); // +2 porque el nivel base es 1
        // Mantener solo los usuarios visitados hasta este nivel
        setVisitedUsers(prev => prev.slice(0, levelIndex + 2));
        refetch();
      }
    }
  };

  // Función para obtener información del nivel actual
  const getCurrentLevelInfo = () => {
    return {
      level: currentLevel,
      levelName: `Nivel ${currentLevel}`,
      isRootLevel: currentLevel === 1,
      navigationDepth: navigationHistory.length
    };
  };

  return {
    isLoading: isLoading,
    isLevelLoading,
    isError: isError,
    isLevelError,
    error,
    userData,
    currentUserId,
    teamMembers: networkUsers,
    navigationHistory,
    currentLevel,
    visitedUsers,
    networkLevels,
    totalUsers,
    totalActiveUsers,
    loadMyNetwork,
    refreshNetwork,
    copyReferralLink,
    shareViaWhatsApp,
    shareViaEmail,
    loadUserNetwork,
    navigateToLevel,
    getCurrentLevelInfo
  };
};
